/**
 * Bill Hooks
 *
 * React Query hooks for bill operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import type {
  Bill,
  BillDetail,
  BillFilters,
  BillTotals,
  Deposit,
  DepositCreate,
  SubmissionAttempt,
  PaginatedResponse,
  CreditNoteCreateRequest,
  DebitNoteCreateRequest,
  NoteResponse,
} from '@/types'

const BILLS_KEY = 'bills'

/**
 * Get paginated list of bills with filters
 */
export function useBills(filters: BillFilters = {}) {
  return useQuery({
    queryKey: [BILLS_KEY, filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters.status) params.append('status_filter', filters.status)
      if (filters.bill_type) params.append('bill_type', filters.bill_type)
      if (filters.client_id) params.append('client_id', String(filters.client_id))
      if (filters.date_from) params.append('date_from', filters.date_from)
      if (filters.date_to) params.append('date_to', filters.date_to)
      if (filters.page) params.append('page', String(filters.page))
      if (filters.page_size) params.append('page_size', String(filters.page_size))

      const response = await api.get<PaginatedResponse<Bill>>(
        `/bills?${params.toString()}`
      )
      return response.data
    },
  })
}

/**
 * Get full bill details including lines, deposits, and Hacienda info
 */
export function useBill(id: number) {
  return useQuery({
    queryKey: [BILLS_KEY, id],
    queryFn: async () => {
      const response = await api.get<BillDetail>(`/bills/${id}`)
      return response.data
    },
    enabled: !!id,
  })
}

/**
 * Get bill totals
 */
export function useBillTotals(id: number) {
  return useQuery({
    queryKey: [BILLS_KEY, id, 'totals'],
    queryFn: async () => {
      const response = await api.get<BillTotals>(`/bills/${id}/totals`)
      return response.data
    },
    enabled: !!id,
  })
}

/**
 * Get deposits for a bill
 */
export function useBillDeposits(billId: number) {
  return useQuery({
    queryKey: [BILLS_KEY, billId, 'deposits'],
    queryFn: async () => {
      const response = await api.get<Deposit[]>(`/bills/${billId}/deposits`)
      return response.data
    },
    enabled: !!billId,
  })
}

/**
 * Get Hacienda submission history for a bill
 */
export function useBillSubmissions(billId: number) {
  return useQuery({
    queryKey: ['invoicing', 'history', billId],
    queryFn: async () => {
      const response = await api.get<{ bill_id: number; attempts: SubmissionAttempt[] }>(
        `/invoicing/history/${billId}`
      )
      return response.data.attempts
    },
    enabled: !!billId,
  })
}

/**
 * Add deposit to a credit bill
 */
export function useAddDeposit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      billId,
      data,
    }: {
      billId: number
      data: DepositCreate
    }) => {
      const response = await api.post<Deposit>(`/bills/${billId}/deposits`, data)
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [BILLS_KEY] })
      queryClient.invalidateQueries({ queryKey: [BILLS_KEY, variables.billId] })
      queryClient.invalidateQueries({
        queryKey: [BILLS_KEY, variables.billId, 'deposits'],
      })
    },
  })
}

/**
 * Submit bill to Hacienda
 */
export function useSubmitToHacienda() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (billId: number) => {
      const response = await api.post(`/invoicing/documents/submit/${billId}`)
      return response.data
    },
    onSuccess: (_, billId) => {
      queryClient.invalidateQueries({ queryKey: [BILLS_KEY, billId] })
      queryClient.invalidateQueries({
        queryKey: ['invoicing', 'history', billId],
      })
      queryClient.invalidateQueries({ queryKey: ['invoicing'] })
    },
  })
}

/**
 * Delete or cancel a bill.
 * - Proformas: hard deleted from DB
 * - Other types: only pending bills, cancelled with stock restore
 */
export function useCancelBill() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (billId: number) => {
      await api.delete(`/bills/${billId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_KEY] })
      queryClient.invalidateQueries({ queryKey: ['proformas'] })
      // Cancelar devuelve el inventario.
      queryClient.invalidateQueries({ queryKey: ['articles'] })
    },
  })
}

/**
 * Update only the free-text client note (works in any bill status).
 */
export function useUpdateBillNote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      billId,
      notes,
    }: {
      billId: number
      notes: string | null
    }) => {
      const response = await api.patch<Bill>(`/bills/${billId}/note`, { notes })
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [BILLS_KEY] })
      queryClient.invalidateQueries({ queryKey: [BILLS_KEY, variables.billId] })
    },
  })
}

/**
 * Correct the receiver activity of an invoice Hacienda doesn't have yet
 * (rejected or never sent), before resending it.
 */
export function useUpdateBillReceiverActivity() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      billId,
      receiverActivityCode,
    }: {
      billId: number
      receiverActivityCode: string | null
    }) => {
      const response = await api.patch<Bill>(`/bills/${billId}/receiver-activity`, {
        receiver_activity_code: receiverActivityCode,
      })
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [BILLS_KEY] })
      queryClient.invalidateQueries({ queryKey: [BILLS_KEY, variables.billId] })
    },
  })
}

/**
 * Finalize a bill
 */
export function useFinalizeBill() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      billId,
      data,
    }: {
      billId: number
      data: {
        payment_type: string
        amount_paid: number
        currency?: string
        term_days?: number
        payment_method_code?: string
      }
    }) => {
      const response = await api.post<Bill>(`/bills/${billId}/finalize`, data)
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [BILLS_KEY] })
      queryClient.invalidateQueries({ queryKey: [BILLS_KEY, variables.billId] })
    },
  })
}

/**
 * Get credit bills report
 */
export function useCreditBills(clientId?: number) {
  return useQuery({
    queryKey: [BILLS_KEY, 'credits', clientId],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (clientId) params.append('client_id', String(clientId))

      const response = await api.get<{
        items: Array<{
          bill_id: number
          bill_number?: string
          client_id?: number
          client_name: string
          total: number
          deposited: number
          debt: number
          created_at: string
        }>
        total_debt: number
        count: number
      }>(`/bills/reports/credits?${params.toString()}`)
      return response.data
    },
  })
}

// ============================================================================
// Hacienda v4.4 Hooks
// ============================================================================

/**
 * Sync document status from Hacienda
 */
export function useSyncHaciendaStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (billId: number) => {
      const response = await api.post<{
        success: boolean
        previous_status: string | null
        current_status: string | null
        message: string
      }>(`/invoicing/documents/sync/${billId}`)
      return response.data
    },
    onSuccess: (_, billId) => {
      queryClient.invalidateQueries({ queryKey: [BILLS_KEY, billId] })
      queryClient.invalidateQueries({ queryKey: ['invoicing', 'history', billId] })
      queryClient.invalidateQueries({ queryKey: ['invoicing'] })
    },
  })
}

/**
 * Invoice details response from Alanube
 */
export interface InvoiceDetails {
  document_key: string
  status: string
  hacienda_status: string | null
  hacienda_message: string | null
  pdf_url: string | null
  xml_url: string | null
  key: string | null
  legal_status: string | null
  government_response: string | null
  error_message: string | null
}

/**
 * Get full invoice details from Alanube (PDF URL, XML URL, status)
 */
// Un documento aceptado o rechazado ya tiene veredicto: consultarlo al
// servicio en cada visita solo sumaba llamadas. Se pide a demanda (PDF/XML,
// Sincronizar).
export const hasFinalHaciendaStatus = (status?: string | null) =>
  status === 'accepted' || status === 'rejected'

interface DetailsOptions {
  auto?: boolean
  knownStatus?: string | null
}

export function useInvoiceDetails(
  documentId: string | null | undefined,
  { auto = true, knownStatus }: DetailsOptions = {},
) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: ['invoicing', 'details', documentId],
    queryFn: async () => {
      if (!documentId) return null
      const response = await api.get<InvoiceDetails>(`/invoicing/documents/details/${documentId}`)
      // El backend guarda el estado nuevo: recargar facturas solo si cambió.
      if (response.data?.hacienda_status !== knownStatus) {
        queryClient.invalidateQueries({ queryKey: ['bills'] })
      }
      return response.data
    },
    enabled: !!documentId && auto,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
  })
}

/**
 * Fetch credit note details from Alanube
 * Uses GET /invoicing/credit-note/details/{id}
 */
export function useCreditNoteDetails(
  documentId: string | null | undefined,
  { auto = true, knownStatus }: DetailsOptions = {},
) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: ['invoicing', 'credit-note-details', documentId],
    queryFn: async () => {
      if (!documentId) return null
      const response = await api.get<InvoiceDetails>(`/invoicing/credit-note/details/${documentId}`)
      if (response.data?.hacienda_status !== knownStatus) {
        queryClient.invalidateQueries({ queryKey: ['bills'] })
      }
      return response.data
    },
    enabled: !!documentId && auto,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
  })
}

/**
 * Fetch ticket details from Alanube
 * Uses GET /invoicing/ticket/details/{id}
 */
export function useTicketDetails(
  documentId: string | null | undefined,
  { auto = true, knownStatus }: DetailsOptions = {},
) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: ['invoicing', 'ticket-details', documentId],
    queryFn: async () => {
      if (!documentId) return null
      const response = await api.get<InvoiceDetails>(`/invoicing/ticket/details/${documentId}`)
      if (response.data?.hacienda_status !== knownStatus) {
        queryClient.invalidateQueries({ queryKey: ['bills'] })
      }
      return response.data
    },
    enabled: !!documentId && auto,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
  })
}

/**
 * Submit Payment Receipt (REP) to Hacienda
 */
export function useSubmitREP() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (depositId: number) => {
      const response = await api.post<{
        success: boolean
        document_key: string | null
        consecutive: string | null
        hacienda_status: string | null
        message: string
      }>(`/invoicing/rep/${depositId}`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_KEY] })
      queryClient.invalidateQueries({ queryKey: ['invoicing'] })
    },
  })
}

/**
 * Cancel document with credit note (100% reversal)
 */
export function useCancelDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (billId: number) => {
      const response = await api.post<{
        success: boolean
        credit_note_id: number | null
        credit_note_key: string | null
        message: string
      }>(`/invoicing/cancel/${billId}`)
      return response.data
    },
    onSuccess: (_, billId) => {
      queryClient.invalidateQueries({ queryKey: [BILLS_KEY] })
      queryClient.invalidateQueries({ queryKey: [BILLS_KEY, billId] })
      queryClient.invalidateQueries({ queryKey: ['invoicing'] })
      queryClient.invalidateQueries({ queryKey: ['articles'] })
    },
  })
}

/**
 * Submit export invoice to Hacienda
 */
export function useSubmitExportInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      billId,
      destinationCountry,
    }: {
      billId: number
      destinationCountry: string
    }) => {
      const response = await api.post<{
        success: boolean
        document_key: string | null
        consecutive: string | null
        hacienda_status: string | null
        message: string
      }>(`/invoicing/export/${billId}?destination_country=${destinationCountry}`)
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [BILLS_KEY, variables.billId] })
      queryClient.invalidateQueries({ queryKey: ['invoicing', 'history', variables.billId] })
      queryClient.invalidateQueries({ queryKey: ['invoicing'] })
    },
  })
}

/**
 * Create a credit note for an accepted invoice
 */
export function useCreateCreditNote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      billId,
      data,
    }: {
      billId: number
      data: CreditNoteCreateRequest
    }) => {
      const response = await api.post<NoteResponse>(`/invoicing/credit-note/${billId}`, data)
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [BILLS_KEY] })
      queryClient.invalidateQueries({ queryKey: [BILLS_KEY, variables.billId] })
      queryClient.invalidateQueries({ queryKey: ['invoicing'] })
      queryClient.invalidateQueries({ queryKey: ['articles'] })
    },
  })
}

/**
 * Create a debit note for an accepted invoice
 */
export function useCreateDebitNote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      billId,
      data,
    }: {
      billId: number
      data: DebitNoteCreateRequest
    }) => {
      const response = await api.post<NoteResponse>(`/invoicing/debit-note/${billId}`, data)
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [BILLS_KEY] })
      queryClient.invalidateQueries({ queryKey: [BILLS_KEY, variables.billId] })
      queryClient.invalidateQueries({ queryKey: ['invoicing'] })
    },
  })
}
